<template>
  <block
    component-name="block-grid-bath-concept"
    data-skin="sand"
    body-class="relative top-0 left-0 grid grid-cols-1 grid-rows-1 text-skin-base h-[calc(100vh-52px)] max-h-[calc(100vh-52px)]"
    :px="false"
    ref="tpl_root"
  >
    <template v-slot:default="blockData">
      <div
        class="relative top-0 left-0 row-start-1 row-end-2 col-start-1 col-end-2 w-full h-full"
      >
        <div class="relative top-0 left-0 w-full h-full">
          <span
            inert
            class="absolute inset-0 z-1 w-full h-full overlay-gradient-from-bottom opacity-50"
          ></span>

          <!-- WEBGL GRID -->
          <grid-bath-concept
            class="relative top-0 left-0 z-2 overflow-clip w-full h-full [&>canvas]:w-full [&>canvas]:h-full [&>canvas]:cursor-grab"
            ref="tpl_webgl"
            :use-intro="coreoState === 'intro'"
            :interactive="webglInteractive"
            :skip-intro="route.name !== 'country-locale-bath-concept'"
            @intro-completed="handleCoreography()"
            @item-clicked="handleItemClick"
          />
          <!-- end WEBGL GRID -->

          <div
            ref="tpl_clickedItemWrapper"
            class="absolute z-3 inset-0 w-full h-full empty:invisible"
            inert
          ></div>
        </div>

        <div
          class="absolute top-0 left-0 z-10 row-start-1 row-end-2 col-start-1 col-end-2 w-full h-full pointer-events-none"
        >
          <!-- CTA -->
          <div
            v-show="coreoState === 'cta'"
            key="cta"
            ref="tpl_cta"
            data-skin="black"
            class="overflow-clip absolute top-0 left-0 w-full h-full flex items-end p-edge text-skin-base pointer-events-auto"
          >
            <span
              ref="tpl_ctaBg"
              class="absolute inset-0 w-full h-full overlay-gradient-from-bottom"
            ></span>

            <div
              ref="tpl_ctaTexts"
              class="relative top-0 left-0 flex flex-col justify-start w-full desktop:w-3/4 pb-viewport-toolbar-overlap"
            >
              <div
                ref="tpl_ctaOverline"
                class="order-1 text-overline/none"
                v-html="$t.labels.bath_concept"
              ></div>

              <wrapper-split-text
                v-if="ctaTitle"
                reveal="words"
                class="order-2 w-full pt-2xs"
              >
                <h1
                  class="text-heading-3 w-full max-w-[1000px]"
                  v-html="$t.titles.bath_concept_cta"
                ></h1>
              </wrapper-split-text>

              <div
                ref="tpl_ctaSubtitle"
                class="order-3 text-body-big mt-sm w-full max-w-[550px] desktop:max-w-[750px]"
                v-html="$t.texts.bath_concept_cta"
              ></div>

              <div ref="tpl_ctaBtn" class="order-4 max-w-min mt-2xl">
                <btn-pill
                  :filled="true"
                  size="big"
                  :title="$t.labels.explore"
                  @click="hideCta"
                />
              </div>
            </div>
          </div>
          <!-- end CTA -->

          <!-- FILTERS -->
          <div
            v-show="coreoState === 'filters'"
            key="filters"
            class="absolute top-0 left-0 w-full h-full flex justify-center items-end px-edge pb-xl"
          >
            <div
              class="flex justify-center pointer-events-auto pb-viewport-toolbar-overlap"
              :class="{
                'translate-y-[150px] transition-transform duration-400 ease-out':
                  $route.name === 'country-locale-bath-concept-id',
              }"
            >
              <btn-pill
                ref="tpl_filtersBtn"
                :filled="true"
                size="big"
                :title="$t.labels.filter_by"
                icon="filters"
                @click.stop="showFilters = true"
              />
            </div>

            <!-- FILTERS MENU MODAL -->
            <modal-bath-concept-filters
              :is-open="showFilters"
              @require-open="showFilters = true"
              @close="showFilters = false"
            />
            <!-- end FILTERS MENU MODAL -->
          </div>
          <!-- end FILTERS -->
        </div>

        <modal-bath-concept-concept
          :is-open="coreoState === 'concept'"
          @close="handleCoreography()"
        />
      </div>
    </template>
  </block>
</template>

<script setup>
import { useRootStore } from "@/stores/root";

const useCta = ref(true);

const rootStore = useRootStore();
const route = useRoute();
const router = useRouter();
const attrs = useAttrs();
const { width: windowW } = useWindowSize();
const { isTablet } = useScreen();

const tpl_root = templateRef("tpl_root");
const tpl_ctaBg = templateRef("tpl_ctaBg");
const tpl_ctaTexts = templateRef("tpl_ctaTexts");
const tpl_ctaOverline = templateRef("tpl_ctaOverline");
const tpl_ctaSubtitle = templateRef("tpl_ctaSubtitle");
const tpl_ctaBtn = templateRef("tpl_ctaBtn");
const tpl_filtersBtn = templateRef("tpl_filtersBtn");
const tpl_clickedItemWrapper = templateRef("tpl_clickedItemWrapper");
const tpl_webgl = templateRef("tpl_webgl");

const showFilters = ref(false);
const showFilterResults = ref(false);
const results = ref(null);
const coreoState = ref(null);
const ctaTitle = ref(false);
const emit = defineEmits(["itemClicked", "enableConceptBtn"]);

function handleCoreography() {
  switch (coreoState.value) {
    case "intro":
      coreoState.value = useCta.value ? "cta" : "concept";
      break;

    case "cta":
      coreoState.value = attrs.config?.skipConcept ? "filters" : "concept";
      break;

    case "concept":
      coreoState.value = "filters";
      break;
  }
}

function hideCta() {
  let tl = gsap.timeline({
    onComplete: () => {
      handleCoreography();
    },
  });
  tl.to(
    tpl_ctaTexts.value,
    { duration: 0.75, y: 25, autoAlpha: 0, ease: "expo.inOut" },
    0
  );
  tl.to(
    tpl_ctaBg.value,
    { duration: 1, opacity: 0, ease: "power1.out" },
    "<0.3"
  );
}

function handleItemClick({ boundingBox, slug, src, type }) {
  if (isTablet.value) {
    const div = document.createElement("div");
    div.style.position = "fixed";
    div.style.top = `${boundingBox.y}px`;
    div.style.left = `${boundingBox.x}px`;
    div.style.width = `${boundingBox.width}px`;
    div.style.height = `${boundingBox.height}px`;
    div.style.zIndex = "9999";
    div.style.pointerEvents = "none";
    div.dataset.type = type;
    div.dataset.needsDesktopEnterAnim = "true";
    div.dataset.slug = slug;

    tpl_clickedItemWrapper.value.appendChild(div);

    emit("itemClicked", div);
  }

  navigateTo(
    `/${route.params.country}/${route.params.locale}/bath-concept/${slug}`
  );
}

const webglInteractive = computed(() => {
  return (
    route.name === "country-locale-bath-concept" &&
    coreoState.value === "filters" &&
    !showFilters.value
  );
});

watch(coreoState, (newVal, oldVal) => {
  switch (newVal) {
    case "cta":
      let tl = gsap.timeline();

      tl.fromTo(
        tpl_ctaBg.value,
        { opacity: 0 },
        { duration: 1, opacity: 1, ease: "power1.out" }
      );

      tl.fromTo(
        tpl_ctaTexts.value,
        { opacity: 0 },
        {
          duration: 0.6,
          opacity: 1,
          ease: "circ.out",
          onStart: () => {
            ctaTitle.value = true;
          },
        }
      );

      tl.fromTo(
        tpl_ctaOverline.value,
        { x: 40, autoAlpha: 0 },
        { duration: 0.6, x: 0, autoAlpha: 1, ease: "expo.inOut" },
        "<"
      );

      tl.fromTo(
        tpl_ctaSubtitle.value,
        { x: 40, autoAlpha: 0 },
        { duration: 0.6, x: 0, autoAlpha: 1, ease: "expo.inOut" },
        "<0.15"
      );

      tl.fromTo(
        tpl_ctaBtn.value,
        { y: 40, autoAlpha: 0 },
        { duration: 0.8, y: 0, autoAlpha: 1, ease: "expo.inOut" },
        "<0.15"
      );
      break;

    case "filters":
      // enable interactivity

      let filtersTL = gsap.timeline({
        onStart: () => {
          emit("enableConceptBtn");
        },
      });

      filtersTL.fromTo(
        tpl_filtersBtn.value.$el,
        { y: 300 },
        { duration: 1, y: 0, ease: "expo.inOut" }
      );
      break;
  }
});

watch(
  () => route.name,
  (newVal, oldVal) => {
    if (
      oldVal === "country-locale-bath-concept-id" &&
      newVal === "country-locale-bath-concept"
    ) {
      tpl_clickedItemWrapper.value.innerHTML = "";
      tpl_webgl.value.play();
    }
  }
);

useSafeMountedEl(
  [
    tpl_root,
    tpl_ctaBg,
    tpl_ctaTexts,
    tpl_ctaOverline,
    tpl_ctaSubtitle,
    tpl_ctaBtn,
    tpl_filtersBtn,
  ],
  () => {
    const hasSkipCta = Object.keys(route.query).includes("skip-cta");

    let cleanQuery;

    if (hasSkipCta) {
      useCta.value = false;
      cleanQuery = Object.fromEntries(
        Object.entries(route.query).filter(([key]) => key !== "skip-cta")
      );
      router.replace({ query: cleanQuery });
    }

    coreoState.value =
      (route.name === "country-locale-bath-concept" &&
        Object.keys(cleanQuery ?? route.query).length) ||
      route.name === "country-locale-bath-concept-id" ||
      rootStore.prevRoute?.name === "country-locale-bath-concept-id"
        ? "filters"
        : "intro";
  }
);
</script>
